import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Paginator } from '@app/classes/paginator';
import { DeprecatedConfirmationDialogComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { IndexView } from '@common/views';
import { BaseForm } from '@forms/base.form';
import { EmployeeJourney } from '@models/employee/employee-journey.model';
import { Employee } from '@models/employee/employee.model';
import { Journey } from '@models/employee/journey.model';

const AnalyticEventsForJourneyItemTypes = {
    salary: AnalyticEvents.EmployeeProfileJourneyTabSalarySave,
    department: AnalyticEvents.EmployeeProfileJourneyTabDepartmentSave,
    'stock_mat-options': AnalyticEvents.EmployeeProfileJourneyTabStockOptionsSave,
    compensation_type: AnalyticEvents.EmployeeProfileJourneyTabCompensationTypeSave,
    employment_type: AnalyticEvents.EmployeeProfileJourneyTabEmploymentTypeSave,
    position: AnalyticEvents.EmployeeProfileJourneyTabPositionSave,
};

@Component({
    templateUrl: './journey-index.template.html',
    styleUrls: ['./journey-index.style.scss'],
})
export class JourneyIndexView extends IndexView implements OnInit {
    @ViewChild('confirmDeleteDialog', { static: true })
    confirmDeleteDialog: DeprecatedConfirmationDialogComponent;
    /**
     * Loading Indicator
     */
    isLoading = true;

    employee: Employee;
    paginator: Paginator;
    userCanDelete: boolean;
    journey: EmployeeJourney[];

    isSelfEmployeeView = false;
    isJourneyDialogOpen = false;

    constructor(
        protected route: ActivatedRoute,
        protected auth: AuthService,
        protected notify: NotifyService,
        protected analyticsService: AnalyticService,
        protected readonly featureService: FeatureService
    ) {
        super();

        this.employee = this.route.snapshot.data.models.get('employee');
        this.userCanDelete = this.auth.isAdmin();
    }

    /**
     * Lifecycle - On Init
     */
    async ngOnInit(): Promise<void> {
        this.loadJourney();
    }

    closeJourneyDialog(): void {
        this.isJourneyDialogOpen = false;
    }

    openJourneyDialog(): void {
        this.isJourneyDialogOpen = true;
    }

    async onCreate(journey: Journey, form: BaseForm): Promise<void> {
        if (form.form.invalid) {
            return;
        }

        this.isLoading = true;

        try {
            this.closeJourneyDialog();
            await new EmployeeJourney({
                db_field: journey.field,
                old_value: journey.oldValue,
                new_value: journey.newValue,
                date_changed: journey.effectiveDate,
                changed_by: this.auth.employee.id,
            })
                .param('company', this.auth.company.id)
                .param('employee', this.employee.id)
                .save();
        } catch (err) {
            this.notify.error(err.message);
            this.isLoading = false;
            this.closeJourneyDialog();
            return;
        }

        // Add Pendo Tracking
        if (!this.isSelfEmployeeView && journey.field in AnalyticEventsForJourneyItemTypes) {
            this.analyticsService.trackEvent(AnalyticEventsForJourneyItemTypes[journey.field]);
        }

        await this.loadJourney();

        form.reset();
        this.notify.success('employees.journey.itemAdded');

        this.isLoading = false;
    }

    async onDelete(employeeJourney: EmployeeJourney): Promise<void> {
        if (!(await this.confirmDeleteDialog.show())) {
            return;
        }

        this.isLoading = true;
        const index = this.journey.findIndex((entry) => entry.id === employeeJourney.id);
        this.journey.splice(index, 1);

        await employeeJourney.param('company', this.auth.company.id).param('employee', this.employee.id).delete();

        // Add Pendo Tracking
        if (!this.isSelfEmployeeView) {
            this.analyticsService.trackEvent(AnalyticEvents.EmployeeProfileJourneyTabDeleteJourney);
        }

        await this.loadJourney();

        this.notify.success('employees.journey.itemDeleted');
        this.isLoading = false;
    }

    private async loadJourney(): Promise<void> {
        this.isLoading = true;

        const [employeeJourney] = await EmployeeJourney.param('company', this.auth.company.id)
            .param('employee', this.employee.id)
            .get();

        this.journey = employeeJourney
            .map((item) => {
                item.date = item.date.date ? item.date.date : item.date;
                return item;
            })
            .reverse();

        this.isLoading = false;
    }
}
